/**
 * DRI Products filter only: section titles + checkboxes in a Popover (no MUI Select multiple).
 * Avoids ListSubheader / Select bugs with optional rows.
 */
import React, { useCallback, useId, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  OutlinedInput,
  Popover,
  Skeleton,
  Typography,
  alpha,
} from '@mui/material';
import { KeyboardArrowDown, SelectAll as SelectAllIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../../../theme';
import { FILTER_UI_STANDARD } from '../../../config/filterUiStandard';
import { InlineSpinner } from '../../common/loading';
import { FILTER_STANDARD, type SelectAllFlatSection } from '../../common/SelectAllMultiSelect';

const MENU_MAX_H = FILTER_UI_STANDARD.menuMaxHeight;
const MENU_MAX_W = FILTER_UI_STANDARD.menuMaxWidth;

/**
 * OutlinedInput `inputComponent` that shows arbitrary React content (same role as Select `renderValue`).
 * MUI passes `value` etc.; we only render `render()` for display.
 */
const PopoverOutlineDisplay = React.forwardRef<HTMLDivElement, Record<string, unknown>>(
  function PopoverOutlineDisplay(props, ref) {
    const { render: renderProp, value: _v, defaultValue: _d, ...rest } = props as {
      render?: () => React.ReactNode;
      value?: unknown;
      defaultValue?: unknown;
    };
    return (
      <Box
        ref={ref}
        component="div"
        {...rest}
        sx={{
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          width: '100%',
          minWidth: 0,
          cursor: 'inherit',
        }}
      >
        {typeof renderProp === 'function' ? renderProp() : null}
      </Box>
    );
  }
);

export interface DriProductSectionSelectProps {
  label: string;
  flatSections: SelectAllFlatSection[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  selectAllValues?: string[];
  renderValue?: (selected: string[]) => React.ReactNode;
}

const DriProductSectionSelect: React.FC<DriProductSectionSelectProps> = ({
  label,
  flatSections,
  selectedValues,
  onChange,
  disabled = false,
  loading = false,
  loadingMessage,
  emptyMessage = 'No options available',
  selectAllValues,
  renderValue: renderValueProp,
}) => {
  const reactId = useId();
  const triggerId = `dri-product-trigger-${reactId}`;
  const labelId = `dri-product-label-${reactId}`;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const labelShrink = open || selectedValues.length > 0;

  const totalItems = flatSections.reduce((n, s) => n + s.items.length, 0);

  const handleClose = useCallback(() => setAnchorEl(null), []);

  const toggle = useCallback(
    (value: string) => {
      if (selectedValues.includes(value)) {
        onChange(selectedValues.filter((v) => v !== value));
      } else {
        onChange([...selectedValues, value]);
      }
    },
    [selectedValues, onChange]
  );

  const handleSelectAll = useCallback(() => {
    if (selectAllValues !== undefined) {
      onChange([...selectAllValues]);
    } else {
      const all = flatSections.flatMap((s) => s.items.map((i) => i.value));
      onChange(all);
    }
  }, [selectAllValues, flatSections, onChange]);

  const handleClearAll = useCallback(() => onChange([]), [onChange]);

  const defaultRenderValue = (selected: string[]) => (
    <Typography
      sx={{
        fontSize: FILTER_STANDARD.fontSize,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        color: selected.length === 0 ? 'text.secondary' : undefined,
      }}
    >
      {selected.length === 0 ? '' : `${selected.length} selected`}
    </Typography>
  );

  if (loading) {
    return (
      <FormControl fullWidth size="small">
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
      </FormControl>
    );
  }

  const displayContent = () =>
    renderValueProp ? renderValueProp(selectedValues) : defaultRenderValue(selectedValues);

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <FormControl fullWidth size="small" variant="outlined" disabled={disabled}>
        <InputLabel id={labelId} htmlFor={triggerId} shrink={labelShrink} sx={{ fontSize: FILTER_STANDARD.fontSize }}>
          {label}
        </InputLabel>
        <OutlinedInput
          id={triggerId}
          label={label}
          notched={labelShrink}
          readOnly
          aria-labelledby={labelId}
          onClick={(e) => {
            if (!disabled) setAnchorEl(e.currentTarget);
          }}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setAnchorEl(e.currentTarget);
            }
          }}
          inputComponent={PopoverOutlineDisplay}
          inputProps={
            {
              render: displayContent,
              role: 'button',
              'aria-haspopup': 'listbox',
              'aria-expanded': open,
            } as React.InputHTMLAttributes<HTMLInputElement>
          }
          endAdornment={
            <InputAdornment position="end" sx={{ pointerEvents: 'none', mr: 0 }}>
              <KeyboardArrowDown sx={{ color: 'action.active', fontSize: '1.25rem' }} />
            </InputAdornment>
          }
          sx={{
            fontSize: FILTER_STANDARD.fontSize,
            cursor: disabled ? 'default' : 'pointer',
            '& .MuiOutlinedInput-input': { py: 1, display: 'flex', alignItems: 'center' },
          }}
        />
      </FormControl>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              maxWidth: MENU_MAX_W,
              width: 'min(100vw - 32px, 320px)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: '#fafafa',
            flexShrink: 0,
          }}
        >
          <Button
            size="small"
            startIcon={<SelectAllIcon sx={{ fontSize: '0.875rem !important' }} />}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectAll();
            }}
            sx={{ fontSize: FILTER_UI_STANDARD.buttonFontSize, minWidth: 'auto', textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Select All
          </Button>
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClearAll();
            }}
            sx={{
              fontSize: FILTER_UI_STANDARD.buttonFontSize,
              minWidth: 'auto',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              color: SEARCE_COLORS.textSecondary,
            }}
          >
            Clear All
          </Button>
        </Box>

        <Box sx={{ maxHeight: MENU_MAX_H, overflow: 'auto' }}>
          {loadingMessage ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
              <InlineSpinner size={16} thickness={5} />
              <Typography sx={{ fontSize: FILTER_STANDARD.fontSize }}>{loadingMessage}</Typography>
            </Box>
          ) : totalItems === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography sx={{ fontSize: FILTER_STANDARD.fontSize, fontStyle: 'italic', color: 'text.secondary' }}>
                {emptyMessage}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding sx={{ py: 0 }}>
              {flatSections.map((section, si) => (
                <React.Fragment key={`sec-${si}-${section.header}`}>
                  <ListSubheader
                    component="div"
                    disableSticky
                    sx={{
                      backgroundColor: section.muted
                        ? alpha(SEARCE_COLORS.neutral_100, 0.05)
                        : alpha(SEARCE_COLORS.primary, 0.08),
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      lineHeight: '32px',
                      py: 0,
                      color: section.muted ? SEARCE_COLORS.neutral_600 : SEARCE_COLORS.primary,
                      ...(section.indentItems ? { pl: 3 } : {}),
                    }}
                  >
                    {section.header}
                  </ListSubheader>
                  {section.items.map((opt) => (
                    <ListItem key={opt.value} disablePadding sx={{ ...(section.indentItems ? { pl: 3 } : {}) }}>
                      <ListItemButton onClick={() => toggle(opt.value)} sx={{ py: 0.5, alignItems: 'center' }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Checkbox
                            edge="start"
                            checked={selectedValues.includes(opt.value)}
                            tabIndex={-1}
                            size="small"
                            sx={{ p: '4px', '& .MuiSvgIcon-root': { fontSize: '1rem' } }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={opt.label}
                          primaryTypographyProps={{
                            sx: {
                              fontSize: FILTER_STANDARD.itemFontSize,
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                              lineHeight: 1.3,
                              ...(section.muted ? { color: alpha(SEARCE_COLORS.neutral_900, 0.7) } : {}),
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default DriProductSectionSelect;
